import { RegistrationFields } from "@/components/events/RegistrationFields";
import { registrationFormValues } from "@/components/events/eventFormValues";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FormDialog } from "@/components/ui/form-dialog";
import { FormSection } from "@/components/ui/form-section";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Info, Clock, Globe, CircleDot, Image as ImageIcon } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useCreateParentEvent } from "@/hooks/useParentEvents";
import { toast } from "sonner";
import type { EventStatus } from "@/types";
import { EventImageManager } from "@/components/events/EventImageManager";

const EVENT_STATUSES: { value: EventStatus; label: string }[] = [
  { value: "approved", label: "Zatwierdzone" },
  { value: "pending", label: "Oczekujące" },
  { value: "rejected", label: "Odrzucone" },
];

const DEFAULT_FORM = {
  ...registrationFormValues({}),
  title: "",
  eventDescription: "",
  date: "",
  endDate: "",
  source: "",
  sourceUrl: "",
  status: "approved" as EventStatus,
  isPreview: false,
};

interface CreateParentEventDialogProps {
  /** Controlled open state. When provided, the default trigger button is hidden. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function CreateParentEventDialog({
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: CreateParentEventDialogProps = {}) {
  const createParent = useCreateParentEvent();
  const isControlled = controlledOpen !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const [form, setForm] = useState(DEFAULT_FORM);
  const [createdId, setCreatedId] = useState<string | null>(null);

  const handleOpenChange = (isOpen: boolean) => {
    if (isControlled) controlledOnOpenChange?.(isOpen);
    else setUncontrolledOpen(isOpen);
    if (!isOpen) {
      setCreatedId(null);
      setForm(DEFAULT_FORM);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result = await createParent.mutateAsync({
        registrationOptions: form.registrationOptions,
        registrationNotes: form.registrationNotes,
        title: form.title,
        eventDescription: form.eventDescription,
        date: new Date(form.date).toISOString(),
        endDate: new Date(form.endDate).toISOString(),
        source: form.source,
        sourceUrl: form.sourceUrl,
        status: form.status,
        isPreview: form.isPreview,
      });
      toast.success("Wydarzenie nadrzędne dodane");
      setCreatedId(result.id);
    } catch (err) {
      toast.error("Nie udało się dodać wydarzenia", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  return (
    <>
      {!isControlled && (
        <Button onClick={() => setUncontrolledOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Dodaj cykl wydarzeń
        </Button>
      )}
      <FormDialog
        open={open}
        onOpenChange={handleOpenChange}
        title={createdId ? "Dodaj zdjęcia" : "Nowy cykl wydarzeń"}
        size="lg"
        footer={
          createdId ? (
            <Button type="button" onClick={() => handleOpenChange(false)}>
              Gotowe
            </Button>
          ) : (
            <>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Anuluj
              </Button>
              <Button
                type="submit"
                form="parent-create-form"
                disabled={createParent.isPending || !form.date || !form.endDate}
              >
                {createParent.isPending ? "Dodawanie..." : "Utwórz"}
              </Button>
            </>
          )
        }
      >
        {createdId ? (
          <>
            <p className="text-sm text-pulso-muted">
              Wydarzenie zostało utworzone. Możesz teraz dodać zdjęcia.
            </p>
            <FormSection icon={ImageIcon} title="Zdjęcia">
              <EventImageManager eventId={createdId} eventsBasePath="/admin/events" />
            </FormSection>
          </>
        ) : (
          <form id="parent-create-form" onSubmit={handleSubmit} className="space-y-4">
            <FormSection icon={Info} title="Podstawowe informacje">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Tytuł</Label>
                  <Input
                    value={form.title}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                    required
                    placeholder="np. Dni Ostrołęki 2026"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Opis</Label>
                  <Textarea
                    value={form.eventDescription}
                    onChange={(e) => setForm((f) => ({ ...f, eventDescription: e.target.value }))}
                    rows={3}
                  />
                </div>
              </div>
            </FormSection>

            <FormSection icon={Clock} title="Termin">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Data rozpoczęcia</Label>
                  <Input
                    type="datetime-local"
                    value={form.date}
                    onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Data zakończenia</Label>
                  <Input
                    type="datetime-local"
                    value={form.endDate}
                    onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
                    required
                  />
                </div>
              </div>
            </FormSection>

            <RegistrationFields
              registrationOptions={form.registrationOptions}
              registrationNotes={form.registrationNotes}
              onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
            />

            <FormSection icon={Globe} title="Źródło" hint="opcjonalne">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nazwa źródła</Label>
                  <Input
                    value={form.source}
                    onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>URL źródła</Label>
                  <Input
                    value={form.sourceUrl}
                    onChange={(e) => setForm((f) => ({ ...f, sourceUrl: e.target.value }))}
                    placeholder="https://..."
                  />
                </div>
              </div>
            </FormSection>

            <FormSection icon={CircleDot} title="Status">
              <Select
                value={form.status}
                onValueChange={(v) => setForm((f) => ({ ...f, status: v as EventStatus }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EVENT_STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormSection>

            <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-950">
              <Checkbox
                id="parent-event-isPreview"
                checked={form.isPreview}
                onCheckedChange={(v) => setForm((f) => ({ ...f, isPreview: !!v }))}
                className="mt-0.5"
              />
              <div className="grid gap-1">
                <Label htmlFor="parent-event-isPreview" className="cursor-pointer font-normal">
                  Tylko podgląd admina
                </Label>
                <p className="text-xs text-muted-foreground">
                  Widoczne tylko dla adminów. Cykl wydarzeń nie pojawi się na publicznej liście.
                </p>
              </div>
            </div>
          </form>
        )}
      </FormDialog>
    </>
  );
}
